import React, { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Mountain, Mail, Award, CheckCircle, TrendingUp, Heart, FileCheck, Briefcase, Landmark, Target, Eye, HandHeart, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="home-container">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-zinc-900/95 backdrop-blur-md shadow-xl' : 'bg-transparent'
      }`}>
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 animate-fade-in">
              <img 
                src="https://customer-assets.emergentagent.com/job_166ab1a5-8f01-460c-943b-4303f928c33d/artifacts/wk6h830f_LogoSinFondo.png" 
                alt="Integralia Logo" 
                className="h-16 w-16 transition-transform duration-300 hover:scale-110"
                style={{ filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))' }}
              />
              <div>
                <h1 className="text-xl font-bold text-amber-400">INTEGRALIA</h1>
                <p className="text-xs text-amber-200">Precooperativa Multiactiva</p>
              </div>
            </div>
            
            <div className="hidden md:flex space-x-8">
              <Link to="inicio" smooth duration={800} className="nav-link">Inicio</Link>
              <Link to="presencia" smooth duration={800} className="nav-link">Territorios</Link>
              <Link to="redes" smooth duration={800} className="nav-link">Fortaleza</Link>
              <button onClick={() => navigate('/solicitudesycontacto')} className="nav-link">Contáctanos</button>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-amber-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <Link 
                to="inicio" 
                smooth 
                duration={800} 
                className="block text-amber-200 hover:text-amber-400 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Inicio
              </Link>
              <Link 
                to="presencia" 
                smooth 
                duration={800} 
                className="block text-amber-200 hover:text-amber-400 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Territorios
              </Link>
              <Link 
                to="redes" 
                smooth 
                duration={800} 
                className="block text-amber-200 hover:text-amber-400 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Fortaleza
              </Link>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/solicitudesycontacto');
                }}
                className="block text-amber-200 hover:text-amber-400 py-2 text-left w-full"
              >
                Contáctanos
              </button>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/31137175/pexels-photo-31137175.jpeg" 
            alt="Minería Tradicional" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-zinc-900"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-6 text-center">
          <div className="animate-slide-up">
            <img 
              src="https://customer-assets.emergentagent.com/job_166ab1a5-8f01-460c-943b-4303f928c33d/artifacts/wk6h830f_LogoSinFondo.png" 
              alt="Integralia Logo" 
              className="h-32 w-32 mx-auto mb-8 animate-float"
              style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))' }}
            />
            <h1 className="hero-title text-5xl md:text-7xl font-bold mb-6">
              Precooperativa Multiactiva
              <span className="block text-amber-400 mt-4">INTEGRALIA</span>
            </h1>
            <p className="hero-subtitle text-xl md:text-2xl text-amber-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              Comprometidos con la minería de subsistencia responsable, trabajando de la mano con redes de mineros ancestrales
            </p>
            <div className="flex flex-wrap gap-4 justify-center items-center mb-8">
              <div className="badge-item">
                <Award className="w-5 h-5" />
                <span>Avalados por Supersolidaria</span>
              </div>
              <div className="badge-item">
                <Shield className="w-5 h-5" />
                <span>Certificación LAFT</span>
              </div>
              <div className="badge-item">
                <Users className="w-5 h-5" />
                <span>+700 Mineros Trabajando con Nosotros</span>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/solicitudesycontacto')}
              className="cta-button text-lg px-8 py-6"
            >
              Contáctanos
              <Mail className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-amber-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-amber-400 rounded-full mt-2 animate-scroll"></div>
          </div>
        </div>
      </section>

      {/* Raíces que Mueven la Tierra Section - MOVED TO SECOND POSITION */}
      <section className="section-padding-reduced bg-zinc-900">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-start mb-12">
            {/* Left Column - Text Content */}
            <div>
              <div className="section-badge mb-6">
                <Landmark className="w-6 h-6" />
                <span>Patrimonio Vivo</span>
              </div>
              
              <h2 className="section-title mb-8">
                Comunidades que guardan el alma de la minería colombiana
              </h2>

              <div className="space-y-6 mb-8">
                <p className="text-lg text-zinc-300 leading-relaxed">
                  Mucho antes de que existieran empresas, títulos o registros, las comunidades indígenas y afrodescendientes del Pacífico colombiano ya conocían cada río, cada veta y cada secreto de la tierra. El barequeo —técnica ancestral de separación manual del oro en las orillas de los ríos— no es solo un oficio: es identidad, memoria colectiva y sustento de generaciones enteras en el Chocó y las regiones del Pacífico.
                </p>

                <p className="text-lg text-zinc-300 leading-relaxed">
                  Estas comunidades son custodias de un conocimiento que ninguna tecnología ha podido reemplazar. Su relación con el territorio es espiritual y productiva a la vez. En Integralia reconocemos que sin ellas no existe minería ancestral, y por eso trabajamos para que sean las primeras beneficiadas: con precios justos, formalización real y acceso al sistema financiero que históricamente les fue negado.
                </p>
              </div>

              {/* Highlight Stats - Horizontal Layout */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="heritage-stat-card">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-amber-400 mb-2">Siglos de historia</h3>
                    <p className="text-zinc-300">
                      El barequeo indígena y afro precede en siglos a cualquier forma de minería industrial en Colombia
                    </p>
                  </CardContent>
                </Card>

                <Card className="heritage-stat-card">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-amber-400 mb-2">Derecho territorial</h3>
                    <p className="text-zinc-300">
                      Las comunidades afro e indígenas tienen reconocimiento constitucional sobre sus territorios y prácticas ancestrales
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="space-y-6">
              <div className="image-card h-80">
                <img 
                  src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/32q9risy_mineria_africa.jpg" 
                  alt="Minería Ancestral África" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="image-card h-80">
                <img 
                  src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/rgv7se41_UUJ4YASRDFE3NJ74AETG5THBCY.jpg" 
                  alt="Minería Tradicional Comunitaria" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Closing Quote */}
          <div className="heritage-quote">
            <div className="quote-accent"></div>
            <blockquote className="quote-content">
              <p className="quote-text">
                &ldquo;La tierra no nos pertenece — nosotros le pertenecemos a ella. Cuidar la minería ancestral es cuidar a quienes la han guardado por siglos.&rdquo;
              </p>
              <footer className="quote-author">
                — Precooperativa Multiactiva Integralia
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Nuestra Presencia en Colombia Section */}
      <section id="presencia" className="section-padding-reduced bg-zinc-900">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="section-badge mb-6 inline-flex">
              <MapPin className="w-6 h-6" />
              <span>Territorios de Acción</span>
            </div>
            <h2 className="section-title mb-6">
              Donde está la minería ancestral, está Integralia
            </h2>
            <p className="text-xl text-zinc-300 max-w-4xl mx-auto leading-relaxed">
              Hacemos presencia activa en tres departamentos estratégicos de Colombia, acompañando a nuestras comunidades mineras con representación comercial, asesoría LAFT y gestión jurídica responsable.
            </p>
          </div>

          {/* Three Department Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* ANTIOQUIA - Using real silhouette image */}
            <div className="department-card group">
              <div className="department-map-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/57v4yj62_antioquia.png"
                  alt="Mapa de Antioquia"
                  className="department-map-image"
                />
              </div>
              <h3 className="department-name">ANTIOQUIA</h3>
              <p className="department-description">
                Cuna de la minería de subsistencia en Colombia. Acompañamos a cientos de mineros artesanales en sus procesos de formalización, representación ante C.I. y acceso al sistema financiero.
              </p>
            </div>

            {/* CHOCÓ - Using real silhouette image */}
            <div className="department-card group">
              <div className="department-map-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/obhegoze_mapa-departamento-choco-division-administrativa-colombia_601298-20619-_1__1-removebg-preview.png"
                  alt="Mapa de Chocó"
                  className="department-map-image"
                />
              </div>
              <h3 className="department-name">CHOCÓ</h3>
              <p className="department-description">
                Territorio de comunidades afrodescendientes e indígenas con siglos de tradición barequera. Nuestra presencia aquí es un compromiso con la historia, la identidad y los derechos territoriales.
              </p>
            </div>

            {/* CALDAS - Using real silhouette image */}
            <div className="department-card group">
              <div className="department-map-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/d17gatd3_caldas.png"
                  alt="Mapa de Caldas"
                  className="department-map-image"
                />
              </div>
              <h3 className="department-name">CALDAS</h3>
              <p className="department-description">
                Región de tradición minera arraigada en sus municipios. Trabajamos para conectar a sus mineros con los mejores precios y garantizar su operación dentro del marco legal vigente.
              </p>
            </div>
          </div>

          {/* Closing Statement */}
          <div className="presence-statement">
            <p className="statement-text">
              Desde Integralia, nos comprometemos a luchar jurídica y responsablemente por cada comunidad minera que representamos. No somos solo intermediarios — somos su voz ante las instituciones, su escudo ante la informalidad y su puente hacia un futuro con derechos, precios justos y dignidad laboral garantizada.
            </p>
            <div className="esal-badge">
              <span>ESAL · Avalada por Supersolidaria · República de Colombia</span>
            </div>
          </div>
        </div>
      </section>

      {/* Misión & Visión Section */}
      <section className="section-padding-reduced bg-zinc-900">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Misión Card */}
            <Card className="mission-vision-card border-l-4 border-l-amber-400">
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-6 h-6 text-amber-400" />
                  <h3 className="text-xl font-bold text-amber-400 uppercase tracking-wide">Misión</h3>
                </div>
                <p className="text-zinc-300 leading-relaxed">
                  Somos una entidad sin ánimo de lucro que representa y acompaña al minero ancestral colombiano. Gestionamos las mejores condiciones comerciales con las Compañías de Intermediación (C.I.), brindamos asesoría integral LAFT y acompañamos a cada minero —incluyendo comunidades afro e indígenas— para que ejerza su actividad con plena legalidad, dignidad y acceso al sistema financiero.
                </p>
              </CardContent>
            </Card>

            {/* Visión Card */}
            <Card className="mission-vision-card border-l-4 border-l-amber-300">
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="w-6 h-6 text-amber-400" />
                  <h3 className="text-xl font-bold text-amber-400 uppercase tracking-wide">Visión</h3>
                </div>
                <p className="text-zinc-300 leading-relaxed">
                  Ser para 2030 la organización solidaria líder en Colombia en la comercialización formal de minerales ancestrales, reconocida por consolidar altos volúmenes de transacción, fortalecer la bancarización sostenible de nuestros mineros y ser aliado estratégico de entidades estatales y del sector financiero en la formalización del sector.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Historia Section */}
      <section className="section-padding-reduced bg-zinc-800">
        <div className="container mx-auto px-6">
          <Card className="historia-card">
            <CardContent className="p-8 md:p-12">
              <h2 className="section-title text-center mb-8">Nacidos de una necesidad real</h2>
              
              <div className="max-w-4xl mx-auto space-y-6 mb-12">
                <p className="text-lg text-zinc-300 leading-relaxed">
                  Integralia nació de observar una realidad que durante décadas fue ignorada: miles de mineros ancestrales en Colombia —hombres y mujeres comunes, comunidades afrodescendientes e indígenas que han trabajado la tierra por generaciones— operaban sin acceso a precios justos, sin acompañamiento legal y sin presencia en el sistema financiero formal.
                </p>
                
                <p className="text-lg text-zinc-300 leading-relaxed">
                  Fundada como precooperativa multiactiva y avalada por la Superintendencia de Economía Solidaria, surgimos con una convicción clara: el minero ancestral merece una organización que lo represente con profesionalismo ante las C.I., que gestione su cumplimiento LAFT y que lo acerque a la bancarización como herramienta de progreso colectivo. Así nació Integralia — de la lucha, para la lucha.
                </p>
              </div>

              {/* Timeline */}
              <div className="timeline-container">
                <div className="timeline-line"></div>
                <div className="timeline-nodes">
                  <div className="timeline-node">
                    <div className="timeline-dot"></div>
                    <p className="timeline-label">Origen</p>
                  </div>
                  <div className="timeline-node">
                    <div className="timeline-dot"></div>
                    <p className="timeline-label">Constitución</p>
                  </div>
                  <div className="timeline-node">
                    <div className="timeline-dot"></div>
                    <p className="timeline-label">Expansión</p>
                  </div>
                  <div className="timeline-node">
                    <div className="timeline-dot"></div>
                    <p className="timeline-label">Visión 2030</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Valores Section */}
      <section className="section-padding-reduced bg-zinc-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="section-title mb-4">Nuestros Valores</h2>
            <p className="text-xl text-zinc-300">Los principios que guían cada una de nuestras acciones</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Integridad */}
            <Card className="valor-card group">
              <CardContent className="p-6">
                <div className="valor-icon-wrapper mb-4">
                  <Shield className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-amber-400 mb-2">Integridad</h3>
                <p className="text-zinc-300">Actuamos con transparencia ante el minero y las entidades</p>
              </CardContent>
            </Card>

            {/* Inclusión */}
            <Card className="valor-card group">
              <CardContent className="p-6">
                <div className="valor-icon-wrapper mb-4">
                  <Heart className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-amber-400 mb-2">Inclusión</h3>
                <p className="text-zinc-300">Comunidades afro, indígenas y trabajadores comunes son nuestra prioridad</p>
              </CardContent>
            </Card>

            {/* Formalidad */}
            <Card className="valor-card group">
              <CardContent className="p-6">
                <div className="valor-icon-wrapper mb-4">
                  <FileCheck className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-amber-400 mb-2">Formalidad</h3>
                <p className="text-zinc-300">Cada minero legalmente constituido y bancarizado</p>
              </CardContent>
            </Card>

            {/* Solidaridad */}
            <Card className="valor-card group">
              <CardContent className="p-6">
                <div className="valor-icon-wrapper mb-4">
                  <HandHeart className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-amber-400 mb-2">Solidaridad</h3>
                <p className="text-zinc-300">El beneficio colectivo por encima del individual</p>
              </CardContent>
            </Card>

            {/* Profesionalismo */}
            <Card className="valor-card group">
              <CardContent className="p-6">
                <div className="valor-icon-wrapper mb-4">
                  <Briefcase className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-amber-400 mb-2">Profesionalismo</h3>
                <p className="text-zinc-300">Gestión de calidad ante C.I., bancos y entidades estatales</p>
              </CardContent>
            </Card>

            {/* Ancestralidad */}
            <Card className="valor-card group">
              <CardContent className="p-6">
                <div className="valor-icon-wrapper mb-4">
                  <Landmark className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-amber-400 mb-2">Ancestralidad</h3>
                <p className="text-zinc-300">Respeto profundo por el saber y oficio milenario</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Redes de Mineros Ancestrales */}
      <section id="redes" className="section-padding-reduced bg-zinc-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-badge mb-6 inline-flex">
              <Users className="w-6 h-6" />
              <span>Nuestra Fortaleza</span>
            </div>
            <h2 className="section-title mb-6">
              Redes de Mineros Ancestrales
            </h2>
            <p className="text-xl text-zinc-300 max-w-3xl mx-auto leading-relaxed">
              Nuestra mayor fortaleza son las <span className="text-amber-400 font-semibold">extensas redes de mineros ancestrales</span> con 
              las que trabajamos, permitiéndonos alcanzar metas de grandes volúmenes mientras preservamos conocimientos tradicionales.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="stat-card group">
              <CardContent className="p-8 text-center">
                <div className="stat-icon-wrapper mb-6">
                  <Users className="w-12 h-12 text-amber-400" />
                </div>
                <h3 className="text-4xl font-bold text-amber-400 mb-2">700+</h3>
                <p className="text-zinc-300">Mineros en nuestra red</p>
              </CardContent>
            </Card>

            <Card className="stat-card group">
              <CardContent className="p-8 text-center">
                <div className="stat-icon-wrapper mb-6">
                  <Mountain className="w-12 h-12 text-amber-400" />
                </div>
                <h3 className="text-4xl font-bold text-amber-400 mb-2">3+</h3>
                <p className="text-zinc-300">Regiones de operación</p>
              </CardContent>
            </Card>

            <Card className="stat-card group">
              <CardContent className="p-8 text-center">
                <div className="stat-icon-wrapper mb-6">
                  <TrendingUp className="w-12 h-12 text-amber-400" />
                </div>
                <h3 className="text-4xl font-bold text-amber-400 mb-2">Alto</h3>
                <p className="text-zinc-300">Volumen de producción</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div className="image-card h-80">
              <img 
                src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/thoio4xb_minera-barequera-choco.jpg" 
                alt="Minera Barequera del Chocó" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="image-card h-80">
              <img 
                src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/6ar50ida_5991c577875d5.jpeg" 
                alt="Comunidad Minera Ancestral Colombia" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contacto" className="section-padding bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="section-badge mb-6 inline-flex">
              <Mail className="w-6 h-6" />
              <span>Contáctanos</span>
            </div>
            <h2 className="section-title mb-6">
              Trabajemos Juntos
            </h2>
            <p className="text-xl text-zinc-300 max-w-2xl mx-auto mb-10">
              ¿Interesado en conocer más sobre nuestras operaciones? Estamos aquí para responder tus preguntas.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6 mb-12">
              <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl p-6 border border-amber-600/20">
                <Mail className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <h3 className="font-semibold text-amber-400 mb-2 text-sm">Email</h3>
                <p className="text-zinc-300 text-xs">administracion@esalintegralia.com</p>
              </div>
              <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl p-6 border border-amber-600/20">
                <Shield className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <h3 className="font-semibold text-amber-400 mb-2 text-sm">Certificación</h3>
                <p className="text-zinc-300 text-xs">Superintendencia de Economía Solidaria</p>
              </div>
              <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl p-6 border border-amber-600/20">
                <Award className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <h3 className="font-semibold text-amber-400 mb-2 text-sm">Especialización</h3>
                <p className="text-zinc-300 text-xs">Minería de Subsistencia - Gestión LAFT</p>
              </div>
            </div>

            <Button 
              onClick={() => navigate('/solicitudesycontacto')}
              className="cta-button text-lg px-10 py-6"
            >
              Enviar Solicitud o Consulta
              <Mail className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="container mx-auto px-6">
          {/* Certification Logos */}
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {/* UIAF - Cumplimiento normativo */}
            <div className="text-center">
              <p className="text-sm text-amber-400 mb-3 font-semibold">Cumplimiento normativo ante:</p>
              <img 
                src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/xfdkv220_imagen_2026-04-02_203630683.png"
                alt="UIAF Colombia"
                className="certification-logo mx-auto"
              />
            </div>

            {/* Supersolidaria - Vigilado por */}
            <div className="text-center">
              <p className="text-sm text-amber-400 mb-3 font-semibold">Vigilado por:</p>
              <img 
                src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/9jzrc3nf_image.png"
                alt="Supersolidaria"
                className="certification-logo mx-auto"
              />
            </div>

            {/* Empresario Modelo 2026 */}
            <div className="text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_solidaria-miners/artifacts/sboxlnjs_SellosRenovacion-2026%20%281%29.png"
                alt="Empresario Modelo 2026"
                className="certification-logo mx-auto"
                style={{ marginTop: '2rem' }}
              />
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-3 mb-6 md:mb-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_166ab1a5-8f01-460c-943b-4303f928c33d/artifacts/wk6h830f_LogoSinFondo.png" 
                  alt="Integralia Logo" 
                  className="h-12 w-12"
                />
                <div>
                  <h3 className="text-lg font-bold text-amber-400">INTEGRALIA</h3>
                  <p className="text-xs text-amber-200">Precooperativa Multiactiva</p>
                </div>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-zinc-400 mb-2">© 2024 Integralia. Todos los derechos reservados.</p>
                <p className="text-sm text-zinc-500">Entidad sin ánimo de lucro - ESAL</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
